import { Box, Button, IconButton, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useParams, useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { resources } from '../../core/services/resources';
import { PageHeader } from '../../shared/components/PageHeader';
import { DataTable } from '../../shared/components/DataTable/DataTable';
import { StatusChip } from '../../shared/components/StatusChip';
import { DetailsInfoTooltip } from '../../shared/components/DetailsInfoTooltip';
import { AddPromptPanel } from '../../shared/components/AddPromptPanel';
import { AddPromptSetPanel } from '../../shared/components/AddPromptSetPanel';
import { extractErrorMessage } from '../../core/services/api';
import { formatDate } from '../../shared/utils/format';
import { flagFor } from '../../shared/utils/geo';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useTenant } from '../../core/contexts/TenantContext';
import {
  ReportTemplateAssignmentDialog,
  type ReportTemplateAssignmentOverrides,
} from './ReportTemplateAssignmentDialog';
import {
  ReportTemplateBulkAttachDialog,
  type BulkAttachValues,
} from './ReportTemplateBulkAttachDialog';
import { ReportTemplatePreviewTab } from './ReportTemplatePreviewTab';
import type {
  Prompt,
  PromptSet,
  ReportTemplatePrompt,
  ReportTemplatePromptSet,
} from '../../core/types/data-models';

export function ReportTemplateDetail() {
  const { id } = useParams();
  const templateId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeTenantId } = useTenant();
  const [tab, setTab] = useState<'prompts' | 'promptSets' | 'preview'>('preview');
  const [attachError, setAttachError] = useState<string | null>(null);
  // Server-side pagination for the prompt / prompt-set tables. 100 rows/page
  // is the backend's max page size; the grid surfaces the total count and
  // next/prev controls. (0-based page for the grid → 1-based for the API.)
  const [promptsPagination, setPromptsPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100,
  });
  const [promptSetsPagination, setPromptSetsPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100,
  });

  const template = useQuery({
    queryKey: ['report-templates', templateId],
    queryFn: () => resources.reportTemplates.get(templateId),
    enabled: Number.isFinite(templateId),
  });

  // Owning Line of Business — its `supported*` arrays cap which geo
  // locations and AI models the assignment dialog may offer, so an
  // assignment can't target a country/model the LOB doesn't run.
  const lineOfBizId = template.data?.lineOfBizId;
  const lineOfBiz = useQuery({
    queryKey: ['lines-of-business', lineOfBizId],
    queryFn: () => resources.linesOfBusiness.get(lineOfBizId as number),
    enabled: lineOfBizId != null,
  });
  const supportedGeoLocationIds = lineOfBiz.data?.supportedGeoLocationIds;
  const supportedAiModelIds = lineOfBiz.data?.supportedAiModelIds;

  const prompts = useQuery({
    queryKey: [
      'report-template-prompts',
      { templateId, page: promptsPagination.page, pageSize: promptsPagination.pageSize },
    ],
    queryFn: () =>
      resources.reportTemplatePrompts.list({
        page: promptsPagination.page + 1,
        pageSize: promptsPagination.pageSize,
        filter: { reportTemplateId: templateId },
        sort: '-id',
      }),
    enabled: Number.isFinite(templateId),
    placeholderData: keepPreviousData,
  });

  const promptSets = useQuery({
    queryKey: [
      'report-template-prompt-sets',
      { templateId, page: promptSetsPagination.page, pageSize: promptSetsPagination.pageSize },
    ],
    queryFn: () =>
      resources.reportTemplatePromptSets.list({
        page: promptSetsPagination.page + 1,
        pageSize: promptSetsPagination.pageSize,
        filter: { reportTemplateId: templateId },
        sort: '-id',
      }),
    enabled: Number.isFinite(templateId),
    placeholderData: keepPreviousData,
  });

  /**
   * Prompt catalog — used for two things:
   *   1. The attached-prompts table renders the prompt's human-readable
   *      `prompt` text via `textById` (junction rows only store the FK,
   *      not the text body).
   *   2. The shared `AddPromptPanel` excludes already-attached prompts;
   *      both the table and the panel point at the same numeric ids now
   *      that the join migrated off `promptIdentifier`.
   */
  const promptCatalog = useQuery({
    queryKey: ['prompts', 'id-index'],
    queryFn: () => resources.prompts.list({ pageSize: 1000 }),
  });
  const textById = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of promptCatalog.data?.data ?? []) map.set(p.id, p.prompt);
    return map;
  }, [promptCatalog.data]);

  // Distinct language codes present in the tenant's prompt catalog — the
  // option set for the Add-prompt panel's Language filter. Sorted for a
  // stable dropdown order.
  const languageOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of promptCatalog.data?.data ?? []) {
      if (p.language) set.add(p.language);
    }
    return Array.from(set).sort();
  }, [promptCatalog.data]);

  // Catalog of countries — shared cache key with `GeoLocationSelect` so the
  // table's flag/name rendering and the dialog's picker fetch only once.
  const geoCatalog = useQuery({
    queryKey: ['geo-locations', 'select'],
    queryFn: () => resources.geoLocations.list({ pageSize: 500, sort: 'name' }),
  });
  const geoById = useMemo(() => {
    const map = new Map<number, { code: string; name: string }>();
    for (const g of geoCatalog.data?.data ?? []) map.set(g.id, { code: g.code, name: g.name });
    return map;
  }, [geoCatalog.data]);

  // Catalog used by the Prompt Sets table to resolve numeric ids → names.
  // Shared cache key with `AddPromptSetPanel`'s picker.
  const promptSetCatalog = useQuery({
    queryKey: ['prompt-sets', 'select'],
    queryFn: () => resources.promptSets.list({ pageSize: 1000, sort: 'name' }),
  });
  const promptSetNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of promptSetCatalog.data?.data ?? []) map.set(s.id, s.name);
    return map;
  }, [promptSetCatalog.data]);

  // FK lookup catalogs for the override columns. Cache keys mirror the
  // pickers in `ReportTemplatePromptDialog`/`ReportTemplateAssignmentDialog`
  // so the dialog and the table share a single fetch per resource.
  const categoryCatalog = useQuery({
    queryKey: ['line-of-biz-categories', 'select'],
    queryFn: () => resources.lineOfBizCategories.list({ pageSize: 500, sort: 'name' }),
  });
  const categoryNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of categoryCatalog.data?.data ?? []) map.set(c.id, c.name);
    return map;
  }, [categoryCatalog.data]);

  const personaCatalog = useQuery({
    queryKey: ['line-of-biz-personas', 'select'],
    queryFn: () => resources.lineOfBizPersonas.list({ pageSize: 500, sort: 'name' }),
  });
  const personaNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of personaCatalog.data?.data ?? []) map.set(p.id, p.name);
    return map;
  }, [personaCatalog.data]);

  const stageCatalog = useQuery({
    queryKey: ['prompt-funnel-stages', 'select'],
    queryFn: () => resources.promptFunnelStages.list({ pageSize: 500, sort: 'name' }),
  });
  const stageNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of stageCatalog.data?.data ?? []) map.set(s.id, s.name);
    return map;
  }, [stageCatalog.data]);

  const brandCatalog = useQuery({
    queryKey: ['tenant-brands', 'select', 'all'],
    queryFn: () => resources.tenantBrands.list({ pageSize: 500, sort: 'name' }),
  });
  const brandNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const b of brandCatalog.data?.data ?? []) map.set(b.id, b.name);
    return map;
  }, [brandCatalog.data]);

  const aiModelCatalog = useQuery({
    queryKey: ['ai-models', 'select'],
    queryFn: () => resources.aiModels.list({ pageSize: 200, sort: 'aiEngine' }),
  });
  const aiModelNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of aiModelCatalog.data?.data ?? []) map.set(m.id, m.name);
    return map;
  }, [aiModelCatalog.data]);

  /**
   * Build a `renderCell` that resolves a single numeric FK id to its name
   * via the supplied lookup map, falling back to `#<id>` while the catalog
   * is loading. Used by the Category/Persona/Stage/Brand columns on both
   * tables.
   */
  function lookupRenderer(map: Map<number, string>) {
    return (p: { value?: unknown }) => {
      const id = p.value as number | null | undefined;
      if (id == null) return '';
      return map.get(id) ?? `#${id}`;
    };
  }

  const attachedPromptIds = useMemo(() => {
    const ids = new Set<number>();
    for (const row of prompts.data?.data ?? []) ids.add(row.promptId);
    return ids;
  }, [prompts.data]);

  // Two-step attach for both Prompts and Prompt Sets: the panel emits
  // the selected entity, we stage it here, then open the overrides
  // dialog. The mutation runs only after the user confirms.
  const [pendingPrompt, setPendingPrompt] = useState<Prompt | null>(null);
  const [pendingPromptSet, setPendingPromptSet] = useState<PromptSet | null>(null);

  // Bulk attach (Prompts tab multi-select): the panel emits the picked
  // prompts, we stage them and open the params dialog. The panel awaits
  // `onAddMany`, so we hold its resolve/reject here and settle it when the
  // dialog saves (resolve → panel clears its selection) or is cancelled
  // (reject → panel keeps the selection so the user can retry).
  const [pendingBulkPrompts, setPendingBulkPrompts] = useState<Prompt[] | null>(null);
  const bulkAttachSettle = useRef<{ resolve: () => void; reject: (e?: unknown) => void } | null>(
    null,
  );

  // Edit mode for an existing assignment row. We open the same overrides
  // dialog used for attaching, just pre-filled from the row and wired to
  // the update mutations below. Keeping `editing*` separate from
  // `pending*` lets both code paths share the dialog without an awkward
  // discriminated union.
  const [editingPrompt, setEditingPrompt] = useState<ReportTemplatePrompt | null>(null);
  const [editingPromptSet, setEditingPromptSet] = useState<ReportTemplatePromptSet | null>(null);

  const attachPrompt = useMutation({
    mutationFn: (args: { prompt: Prompt; overrides: ReportTemplateAssignmentOverrides }) =>
      resources.reportTemplatePrompts.create({
        reportTemplateId: templateId,
        promptId: args.prompt.id,
        status: args.overrides.status,
        geoLocationIds: args.overrides.geoLocationIds,
        aiModelIds: args.overrides.aiModelIds,
        categoryId: args.overrides.categoryId,
        personaId: args.overrides.personaId,
        stageId: args.overrides.stageId,
        brandId: args.overrides.brandId,
        importance: args.overrides.importance,
        // Tenant-scoped row — Prisma extension also injects this from JWT,
        // but explicitly setting keeps create payloads predictable.
        ...(activeTenantId != null ? { tenantId: activeTenantId } : {}),
      } as Partial<ReportTemplatePrompt>),
    onSuccess: () => {
      setAttachError(null);
      setPendingPrompt(null);
      queryClient.invalidateQueries({ queryKey: ['report-template-prompts', { templateId }] });
    },
    onError: (err) => setAttachError(extractErrorMessage(err)),
  });

  // Batch attach — fired after the Prompts tab's params dialog is saved.
  // The dialog's `values` apply to every prompt: geo + AI models are set
  // for the whole batch, while category / persona / stage / importance
  // either keep each prompt's own default (`useDefault`) or take the
  // chosen override. Brand always keeps the prompt's default (no batch
  // control). We attach sequentially so a mid-batch failure surfaces
  // cleanly and the already-created rows aren't left racing the
  // invalidation.
  const attachPromptsBulk = useMutation({
    mutationFn: async (args: { prompts: Prompt[]; values: BulkAttachValues }) => {
      const { prompts, values } = args;
      for (const prompt of prompts) {
        await resources.reportTemplatePrompts.create({
          reportTemplateId: templateId,
          promptId: prompt.id,
          status: 'ACTIVE',
          geoLocationIds: values.geoLocationIds,
          aiModelIds: values.aiModelIds,
          categoryId: values.category.useDefault
            ? prompt.defaultCategoryId
            : values.category.value,
          personaId: values.persona.useDefault
            ? prompt.defaultPersonaId
            : values.persona.value,
          stageId: values.stage.useDefault ? prompt.defaultStageId : values.stage.value,
          brandId: prompt.defaultBrandId,
          importance: values.importance.useDefault
            ? prompt.defaultImportance
            : values.importance.value,
          ...(activeTenantId != null ? { tenantId: activeTenantId } : {}),
        } as Partial<ReportTemplatePrompt>);
      }
    },
    onSuccess: () => {
      setAttachError(null);
      queryClient.invalidateQueries({ queryKey: ['report-template-prompts', { templateId }] });
    },
    onError: (err) => setAttachError(extractErrorMessage(err)),
  });

  const attachPromptSet = useMutation({
    mutationFn: (args: { promptSet: PromptSet; overrides: ReportTemplateAssignmentOverrides }) =>
      resources.reportTemplatePromptSets.create({
        reportTemplateId: templateId,
        promptSetId: args.promptSet.id,
        status: args.overrides.status,
        geoLocationIds: args.overrides.geoLocationIds,
        aiModelIds: args.overrides.aiModelIds,
        categoryId: args.overrides.categoryId,
        personaId: args.overrides.personaId,
        stageId: args.overrides.stageId,
        brandId: args.overrides.brandId,
        ...(activeTenantId != null ? { tenantId: activeTenantId } : {}),
      } as Partial<ReportTemplatePromptSet>),
    onSuccess: () => {
      setAttachError(null);
      setPendingPromptSet(null);
      queryClient.invalidateQueries({ queryKey: ['report-template-prompt-sets', { templateId }] });
    },
    onError: (err) => setAttachError(extractErrorMessage(err)),
  });

  // Set ids already attached — passed to AddPromptSetPanel so the picker
  // hides duplicates client-side.
  const attachedPromptSetIds = useMemo(
    () => new Set((promptSets.data?.data ?? []).map((r) => r.promptSetId)),
    [promptSets.data],
  );

  // Update existing assignments via the standard PUT on the top-level
  // CRUD endpoints. Only the override-y fields are sent — we deliberately
  // leave `reportTemplateId`/`promptId`/`promptSetId` alone so the
  // join's identity can't accidentally change from the edit dialog.
  const updatePrompt = useMutation({
    mutationFn: (args: { id: number; overrides: ReportTemplateAssignmentOverrides }) =>
      resources.reportTemplatePrompts.update(args.id, {
        status: args.overrides.status,
        geoLocationIds: args.overrides.geoLocationIds,
        aiModelIds: args.overrides.aiModelIds,
        categoryId: args.overrides.categoryId,
        personaId: args.overrides.personaId,
        stageId: args.overrides.stageId,
        brandId: args.overrides.brandId,
        importance: args.overrides.importance,
      } as Partial<ReportTemplatePrompt>),
    onSuccess: () => {
      setAttachError(null);
      setEditingPrompt(null);
      queryClient.invalidateQueries({ queryKey: ['report-template-prompts', { templateId }] });
    },
    onError: (err) => setAttachError(extractErrorMessage(err)),
  });

  const updatePromptSet = useMutation({
    mutationFn: (args: { id: number; overrides: ReportTemplateAssignmentOverrides }) =>
      resources.reportTemplatePromptSets.update(args.id, {
        status: args.overrides.status,
        geoLocationIds: args.overrides.geoLocationIds,
        aiModelIds: args.overrides.aiModelIds,
        categoryId: args.overrides.categoryId,
        personaId: args.overrides.personaId,
        stageId: args.overrides.stageId,
        brandId: args.overrides.brandId,
      } as Partial<ReportTemplatePromptSet>),
    onSuccess: () => {
      setAttachError(null);
      setEditingPromptSet(null);
      queryClient.invalidateQueries({ queryKey: ['report-template-prompt-sets', { templateId }] });
    },
    onError: (err) => setAttachError(extractErrorMessage(err)),
  });

  // Detach (delete junction row) — server-side cascade just deletes the
  // join, not the prompt set itself.
  const detachPromptSet = useMutation({
    mutationFn: (rowId: number) => resources.reportTemplatePromptSets.remove(rowId),
    onSuccess: () => {
      setAttachError(null);
      queryClient.invalidateQueries({ queryKey: ['report-template-prompt-sets', { templateId }] });
    },
    onError: (err) => setAttachError(extractErrorMessage(err)),
  });

  // Detach by junction-row id — server-side cascade just deletes the row
  // (not the prompt itself). Errors are surfaced via the same alert as
  // attach so failed deletes are visible.
  const detachPrompt = useMutation({
    mutationFn: (rowId: number) => resources.reportTemplatePrompts.remove(rowId),
    onSuccess: () => {
      setAttachError(null);
      queryClient.invalidateQueries({ queryKey: ['report-template-prompts', { templateId }] });
    },
    onError: (err) => setAttachError(extractErrorMessage(err)),
  });

  const promptColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    {
      field: 'promptId',
      headerName: 'Prompt',
      flex: 1,
      minWidth: 320,
      // Show the human-readable prompt text. Falls back to `#<id>` while
      // the catalog is loading or for ids outside the loaded page.
      valueGetter: (_v, row: ReportTemplatePrompt) =>
        textById.get(row.promptId) ?? `#${row.promptId}`,
    },
    { field: 'status', headerName: 'Status', width: 120, renderCell: (p) => <StatusChip value={p.value as string} /> },
    {
      field: 'aiModelIds',
      headerName: 'AI models',
      width: 200,
      // Postgres int[] of `AIModel.id` — resolve to display names.
      renderCell: (p) => {
        const ids = (p.value as number[] | null | undefined) ?? [];
        if (ids.length === 0) return '';
        return ids.map((id) => aiModelNameById.get(id) ?? `#${id}`).join(', ');
      },
    },
    {
      field: 'geoLocationIds',
      headerName: 'Geo',
      width: 220,
      // Now a Postgres int[] — render flag-emoji per id; fall back to
      // `#<id>` while the catalog is loading or for unseeded ids.
      renderCell: (p) => {
        const ids = (p.value as number[] | null | undefined) ?? [];
        if (ids.length === 0) return '';
        return ids
          .map((id) => {
            const geo = geoById.get(id);
            return geo ? `${flagFor(geo.code)} ${geo.code}` : `#${id}`;
          })
          .join('  ');
      },
    },
    // Resolve numeric FKs → human-readable names via the shared catalog
    // lookups above (Category/Persona/Stage). Falls back to `#<id>` while
    // the catalog is loading or for ids outside the current page. Brand is
    // intentionally omitted from this table.
    { field: 'categoryId', headerName: 'Category', width: 160, renderCell: lookupRenderer(categoryNameById) },
    { field: 'personaId',  headerName: 'Persona',  width: 140, renderCell: lookupRenderer(personaNameById) },
    { field: 'stageId',    headerName: 'Stage',    width: 160, renderCell: lookupRenderer(stageNameById) },
    {
      field: 'importance',
      headerName: 'Importance',
      width: 120,
      renderCell: (p) => <StatusChip value={p.value as string} />,
    },
    {
      field: '__actions',
      headerName: '',
      sortable: false,
      filterable: false,
      // Wide enough for two `size="small"` IconButtons + the DataGrid's
      // built-in cell padding. Was 110 — bumped to 130 so on tighter
      // viewports the delete icon doesn't get clipped behind the column
      // edge or hidden under a horizontal scroll.
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        // Edit (re-opens the assignment dialog pre-filled) + Detach.
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ height: '100%' }}
        >
          <IconButton
            size="small"
            onClick={() => {
              setAttachError(null);
              setEditingPrompt(p.row as ReportTemplatePrompt);
            }}
            aria-label="edit prompt assignment"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            disabled={detachPrompt.isPending}
            onClick={() => detachPrompt.mutate((p.row as ReportTemplatePrompt).id)}
            aria-label="detach prompt"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const promptSetColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    {
      field: 'promptSetId',
      headerName: 'Prompt set',
      flex: 1,
      minWidth: 240,
      // Resolve numeric id to the set's display name via the same shared
      // `['prompt-sets', 'select']` cache used elsewhere.
      valueGetter: (_v, row: ReportTemplatePromptSet) =>
        promptSetNameById.get(row.promptSetId) ?? `#${row.promptSetId}`,
    },
    { field: 'status', headerName: 'Status', width: 120, renderCell: (p) => <StatusChip value={p.value as string} /> },
    {
      field: 'aiModelIds',
      headerName: 'AI models',
      width: 200,
      renderCell: (p) => {
        const ids = (p.value as number[] | null | undefined) ?? [];
        if (ids.length === 0) return '';
        return ids.map((id) => aiModelNameById.get(id) ?? `#${id}`).join(', ');
      },
    },
    {
      field: 'geoLocationIds',
      headerName: 'Geo',
      width: 260,
      renderCell: (p) => {
        const ids = (p.value as number[] | null | undefined) ?? [];
        if (ids.length === 0) return '';
        return ids
          .map((id) => {
            const geo = geoById.get(id);
            return geo ? `${flagFor(geo.code)} ${geo.name}` : `#${id}`;
          })
          .join(', ');
      },
    },
    // Resolve numeric FKs → human-readable names via the shared catalog
    // lookups above (Category/Persona/Stage/Brand). Falls back to `#<id>`
    // while the catalog is loading or for ids outside the current page.
    { field: 'categoryId', headerName: 'Category', width: 160, renderCell: lookupRenderer(categoryNameById) },
    { field: 'personaId',  headerName: 'Persona',  width: 140, renderCell: lookupRenderer(personaNameById) },
    { field: 'stageId',    headerName: 'Stage',    width: 160, renderCell: lookupRenderer(stageNameById) },
    { field: 'brandId',    headerName: 'Brand',    width: 160, renderCell: lookupRenderer(brandNameById) },
    {
      field: '__actions',
      headerName: '',
      sortable: false,
      filterable: false,
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ height: '100%' }}
        >
          <IconButton
            size="small"
            onClick={() => {
              setAttachError(null);
              setEditingPromptSet(p.row as ReportTemplatePromptSet);
            }}
            aria-label="edit prompt-set assignment"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            disabled={detachPromptSet.isPending}
            onClick={() => detachPromptSet.mutate((p.row as ReportTemplatePromptSet).id)}
            aria-label="detach prompt set"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5" fontWeight={600}>
              {template.data?.name ?? `Report template #${templateId}`}
            </Typography>
            <DetailsInfoTooltip
              rows={[
                { label: 'ID', value: template.data?.id ?? '—' },
                { label: 'LOB', value: template.data?.lineOfBizId ?? '—' },
                {
                  label: 'Priority',
                  value: <StatusChip value={template.data?.defaultPriority} />,
                },
                {
                  label: 'Status',
                  value: <StatusChip value={template.data?.status} />,
                },
                {
                  label: 'Schedule',
                  value: template.data?.schedule?.runType ?? '—',
                },
                {
                  label: 'Relevant period',
                  value:
                    template.data?.relevantPeriod == null
                      ? '—'
                      : `${template.data.relevantPeriod} day${template.data.relevantPeriod === 1 ? '' : 's'}`,
                },
                { label: 'Created', value: formatDate(template.data?.createdAt) },
                { label: 'Updated', value: formatDate(template.data?.updatedAt) },
              ]}
            />
          </Stack>
        }
        description={template.data?.description ?? undefined}
        actions={<Button onClick={() => navigate(-1)}>Back</Button>}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
          <Tab label="Preview" value="preview" />
          <Tab label="Prompts" value="prompts" />
          <Tab label="Prompt sets" value="promptSets" />
        </Tabs>

        {tab === 'preview' ? (
          // Aggregated view of every prompt this template would run —
          // direct attachments plus the contents of every attached set.
          <ReportTemplatePreviewTab
            templateId={templateId}
            categoryNameById={categoryNameById}
            personaNameById={personaNameById}
            stageNameById={stageNameById}
            brandNameById={brandNameById}
            promptSetNameById={promptSetNameById}
            promptTextById={textById}
            geoById={geoById}
          />
        ) : tab === 'prompts' ? (
          // Tab body is a flex column: the search panel sits on top
          // (its own height), the attached-prompts table fills the rest.
          <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            <AddPromptPanel
              multiple
              addButtonLabel="Add to template"
              excludePromptIds={attachedPromptIds}
              // Report-template prompts filter by language + importance,
              // not by brand (the brand override lives on the assignment).
              showBrandFilter={false}
              showLanguageFilter
              languageOptions={languageOptions}
              showImportanceFilter
              error={attachError}
              onErrorClose={() => setAttachError(null)}
              isAdding={pendingBulkPrompts != null || attachPromptsBulk.isPending}
              // Multi-select mode: clicking the button opens the params
              // dialog. We return a promise the dialog settles — resolved
              // on a successful attach (the panel then clears its picks),
              // rejected on cancel (the panel keeps them for a retry).
              onAddMany={(prompts) =>
                new Promise<void>((resolve, reject) => {
                  setAttachError(null);
                  setPendingBulkPrompts(prompts);
                  bulkAttachSettle.current = { resolve, reject };
                })
              }
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <DataTable<ReportTemplatePrompt>
                rows={prompts.data?.data ?? []}
                columns={promptColumns}
                loading={prompts.isFetching}
                paginationMode="server"
                rowCount={prompts.data?.page.total ?? 0}
                paginationModel={promptsPagination}
                onPaginationModelChange={setPromptsPagination}
              />
            </Box>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            <AddPromptSetPanel
              addButtonLabel="Add to template"
              excludePromptSetIds={attachedPromptSetIds}
              error={attachError}
              onErrorClose={() => setAttachError(null)}
              isAdding={attachPromptSet.isPending}
              onAdd={(set) => {
                setAttachError(null);
                setPendingPromptSet(set);
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <DataTable<ReportTemplatePromptSet>
                rows={promptSets.data?.data ?? []}
                columns={promptSetColumns}
                loading={promptSets.isFetching}
                paginationMode="server"
                rowCount={promptSets.data?.page.total ?? 0}
                paginationModel={promptSetsPagination}
                onPaginationModelChange={setPromptSetsPagination}
              />
            </Box>
          </Stack>
        )}
      </Box>

      {/* Attach-prompt dialog: prompt's own defaults seed the overrides. */}
      <ReportTemplateAssignmentDialog
        open={pendingPrompt != null}
        title="Attach prompt"
        saving={attachPrompt.isPending}
        error={attachError}
        onErrorClose={() => setAttachError(null)}
        showImportance
        supportedGeoLocationIds={supportedGeoLocationIds}
        supportedAiModelIds={supportedAiModelIds}
        header={
          pendingPrompt ? (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Prompt
              </Typography>
              <Typography variant="body2">{pendingPrompt.prompt}</Typography>
            </Paper>
          ) : undefined
        }
        defaults={
          pendingPrompt
            ? {
                categoryId: pendingPrompt.defaultCategoryId,
                personaId: pendingPrompt.defaultPersonaId,
                stageId: pendingPrompt.defaultStageId,
                brandId: pendingPrompt.defaultBrandId,
                importance: pendingPrompt.defaultImportance,
              }
            : undefined
        }
        onClose={() => {
          if (attachPrompt.isPending) return;
          setPendingPrompt(null);
          setAttachError(null);
        }}
        onSave={async (overrides) => {
          if (!pendingPrompt) return;
          await attachPrompt.mutateAsync({ prompt: pendingPrompt, overrides });
        }}
      />

      {/* Attach-prompt-set dialog: no per-prompt defaults exist on a set,
          so the form starts blank (status defaults to ACTIVE). */}
      <ReportTemplateAssignmentDialog
        open={pendingPromptSet != null}
        title="Attach prompt set"
        saving={attachPromptSet.isPending}
        error={attachError}
        onErrorClose={() => setAttachError(null)}
        supportedGeoLocationIds={supportedGeoLocationIds}
        supportedAiModelIds={supportedAiModelIds}
        header={
          pendingPromptSet ? (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Prompt set
              </Typography>
              <Typography variant="body2" fontWeight={600}>{pendingPromptSet.name}</Typography>
              {pendingPromptSet.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {pendingPromptSet.description}
                </Typography>
              )}
            </Paper>
          ) : undefined
        }
        onClose={() => {
          if (attachPromptSet.isPending) return;
          setPendingPromptSet(null);
          setAttachError(null);
        }}
        onSave={async (overrides) => {
          if (!pendingPromptSet) return;
          await attachPromptSet.mutateAsync({ promptSet: pendingPromptSet, overrides });
        }}
      />

      {/* Edit-prompt-assignment dialog: same form, pre-filled from the
          existing junction row, wired to the update mutation. The row's
          current settings (override fields + status) seed `defaults`, so
          unchanged fields round-trip unchanged. */}
      <ReportTemplateAssignmentDialog
        open={editingPrompt != null}
        title="Edit prompt assignment"
        submitLabel="Save"
        saving={updatePrompt.isPending}
        error={attachError}
        onErrorClose={() => setAttachError(null)}
        showImportance
        supportedGeoLocationIds={supportedGeoLocationIds}
        supportedAiModelIds={supportedAiModelIds}
        header={
          editingPrompt ? (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Prompt
              </Typography>
              <Typography variant="body2">
                {textById.get(editingPrompt.promptId) ?? `#${editingPrompt.promptId}`}
              </Typography>
            </Paper>
          ) : undefined
        }
        defaults={
          editingPrompt
            ? {
                status: editingPrompt.status,
                geoLocationIds: editingPrompt.geoLocationIds ?? [],
                aiModelIds: editingPrompt.aiModelIds ?? [],
                categoryId: editingPrompt.categoryId,
                personaId: editingPrompt.personaId,
                stageId: editingPrompt.stageId,
                brandId: editingPrompt.brandId,
                importance: editingPrompt.importance,
              }
            : undefined
        }
        onClose={() => {
          if (updatePrompt.isPending) return;
          setEditingPrompt(null);
          setAttachError(null);
        }}
        onSave={async (overrides) => {
          if (!editingPrompt) return;
          await updatePrompt.mutateAsync({ id: editingPrompt.id, overrides });
        }}
      />

      {/* Edit-prompt-set-assignment dialog. Mirrors the prompt edit one
          above; PromptSets have no per-set defaults so the seed comes
          entirely from the row itself. */}
      <ReportTemplateAssignmentDialog
        open={editingPromptSet != null}
        title="Edit prompt set assignment"
        submitLabel="Save"
        saving={updatePromptSet.isPending}
        error={attachError}
        onErrorClose={() => setAttachError(null)}
        supportedGeoLocationIds={supportedGeoLocationIds}
        supportedAiModelIds={supportedAiModelIds}
        header={
          editingPromptSet ? (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Prompt set
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {promptSetNameById.get(editingPromptSet.promptSetId) ?? `#${editingPromptSet.promptSetId}`}
              </Typography>
            </Paper>
          ) : undefined
        }
        defaults={
          editingPromptSet
            ? {
                status: editingPromptSet.status,
                geoLocationIds: editingPromptSet.geoLocationIds ?? [],
                aiModelIds: editingPromptSet.aiModelIds ?? [],
                categoryId: editingPromptSet.categoryId,
                personaId: editingPromptSet.personaId,
                stageId: editingPromptSet.stageId,
                brandId: editingPromptSet.brandId,
              }
            : undefined
        }
        onClose={() => {
          if (updatePromptSet.isPending) return;
          setEditingPromptSet(null);
          setAttachError(null);
        }}
        onSave={async (overrides) => {
          if (!editingPromptSet) return;
          await updatePromptSet.mutateAsync({ id: editingPromptSet.id, overrides });
        }}
      />

      {/* Bulk attach-prompts dialog: define the params once and apply them
          to every selected prompt. Category/persona/stage/importance keep
          each prompt's own default unless overridden. */}
      <ReportTemplateBulkAttachDialog
        open={pendingBulkPrompts != null}
        promptCount={pendingBulkPrompts?.length ?? 0}
        saving={attachPromptsBulk.isPending}
        error={attachError}
        onErrorClose={() => setAttachError(null)}
        supportedGeoLocationIds={supportedGeoLocationIds}
        supportedAiModelIds={supportedAiModelIds}
        onClose={() => {
          if (attachPromptsBulk.isPending) return;
          // Cancelled — reject the panel's promise so it keeps the picks.
          bulkAttachSettle.current?.reject(new Error('cancelled'));
          bulkAttachSettle.current = null;
          setPendingBulkPrompts(null);
          setAttachError(null);
        }}
        onSave={async (values) => {
          if (!pendingBulkPrompts) return;
          await attachPromptsBulk.mutateAsync({ prompts: pendingBulkPrompts, values });
          // Success — let the panel clear its selection, then close.
          bulkAttachSettle.current?.resolve();
          bulkAttachSettle.current = null;
          setPendingBulkPrompts(null);
        }}
      />
    </Box>
  );
}
